import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers, raw = false } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        // FormData sets its own multipart Content-Type (with boundary) —
        // never override it.
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    // `raw: true` returns the whole envelope (data + meta, e.g. pagination
    // totals) instead of unwrapping to just `data`.
    if (raw) return payload;
    return payload?.data ?? payload;
  }

  /** Fetches an authenticated binary endpoint and returns a Blob (e.g. a stored PDF). */
  async requestBlob(endpoint) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const error = new Error(`Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return res.blob();
  }

  // ---- Auth ----
  register(payload) {
    return this.request('/auth/register', { method: 'POST', body: payload });
  }

  verifyEmail(token) {
    return this.request('/auth/verify-email', { method: 'POST', body: { token } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  forgotPassword(email) {
    return this.request('/auth/forgot-password', { method: 'POST', body: { email } });
  }

  resetPassword({ token, password, confirmPassword }) {
    return this.request('/auth/reset-password', { method: 'POST', body: { token, password, confirmPassword } });
  }

  validateResetToken(token) {
    return this.request(`/auth/reset-password/token-status?token=${encodeURIComponent(token)}`);
  }

  me() {
    return this.request('/auth/me');
  }

  changePassword({ currentPassword, newPassword, confirmPassword }) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword, confirmPassword },
    });
  }

  // ---- Users (Admin roster) ----
  listUsers({ limit = 50, offset = 0, search = '', role = '', status = 'all' } = {}) {
    const params = new URLSearchParams({ limit, offset, search, role, status });
    return this.request(`/users?${params.toString()}`, { raw: true });
  }

  inviteUser({ name, email, roleIds }) {
    return this.request('/users', { method: 'POST', body: { name, email, roleIds } });
  }

  updateUser(id, { name, email, profession }) {
    return this.request(`/users/${id}`, { method: 'PATCH', body: { name, email, profession } });
  }

  setUserRoles(id, roleIds) {
    return this.request(`/users/${id}/roles`, { method: 'PUT', body: { roleIds } });
  }

  setUserActive(id, isActive) {
    return this.request(`/users/${id}/status`, { method: 'PATCH', body: { isActive } });
  }

  // ---- Roles (Admin) ----
  listRoles() {
    return this.request('/roles');
  }

  createRole({ name, description, copyFromRoleId }) {
    return this.request('/roles', { method: 'POST', body: { name, description, copyFromRoleId } });
  }

  updateRole(id, { name, description }) {
    return this.request(`/roles/${id}`, { method: 'PATCH', body: { name, description } });
  }

  deleteRole(id) {
    return this.request(`/roles/${id}`, { method: 'DELETE' });
  }

  getRolePermissions(roleId) {
    return this.request(`/roles/${roleId}/permissions`);
  }

  updateRolePermissions(roleId, grants) {
    return this.request(`/roles/${roleId}/permissions`, { method: 'PUT', body: { grants } });
  }

  // ---- Counterparties (Account Officer / Agency Manager / Admin) ----
  listCounterparties({ search = '', type = '', status = '', officerId = '', accreditationState = '' } = {}) {
    const params = new URLSearchParams({ search, type, status, officerId, accreditationState });
    return this.request(`/counterparties?${params.toString()}`);
  }

  // "Review counterparty portfolio and accreditation health" (Agency Manager) — AC1's summary cards.
  getCounterpartyHealthSummary() {
    return this.request('/counterparties/health-summary');
  }

  // Minimal {id, name} picker list of active Account Officers — scoped to
  // the counterparties module's own role gate, unlike the Admin-only
  // /users roster.
  listCounterpartyOfficers() {
    return this.request('/counterparties/officers');
  }

  getCounterparty(id) {
    return this.request(`/counterparties/${id}`);
  }

  createCounterparty(payload) {
    return this.request('/counterparties', { method: 'POST', body: payload });
  }

  updateCounterparty(id, payload) {
    return this.request(`/counterparties/${id}`, { method: 'PATCH', body: payload });
  }

  addPlacementSite(counterpartyId, payload) {
    return this.request(`/counterparties/${counterpartyId}/sites`, { method: 'POST', body: payload });
  }

  uploadAccreditation(counterpartyId, { credentialType, issueDate, expiryDate, file }) {
    const form = new FormData();
    form.append('credentialType', credentialType);
    form.append('issueDate', issueDate);
    form.append('expiryDate', expiryDate);
    form.append('file', file);
    return this.request(`/counterparties/${counterpartyId}/accreditations`, { method: 'POST', body: form });
  }

  downloadAccreditationFile(counterpartyId, accreditationId) {
    return this.requestBlob(`/counterparties/${counterpartyId}/accreditations/${accreditationId}/file`);
  }

  // ---- Demand Orders (Account Officer / Agency Manager / Admin) ----
  listDemandOrders({ search = '', corridorPack = '', state = '', expiryHorizon = '' } = {}) {
    const params = new URLSearchParams({ search, corridorPack, state, expiryHorizon });
    return this.request(`/demand-orders?${params.toString()}`);
  }

  getDemandOrder(id) {
    return this.request(`/demand-orders/${id}`);
  }

  createDemandOrder(payload) {
    return this.request('/demand-orders', { method: 'POST', body: payload });
  }

  addDemandOrderLine(demandOrderId, payload) {
    return this.request(`/demand-orders/${demandOrderId}/lines`, { method: 'POST', body: payload });
  }

  transitionDemandOrderState(demandOrderId, payload) {
    return this.request(`/demand-orders/${demandOrderId}/transitions`, { method: 'POST', body: payload });
  }

  addSubAllocation(demandOrderId, payload) {
    return this.request(`/demand-orders/${demandOrderId}/sub-allocations`, { method: 'POST', body: payload });
  }

  recordSubAllocationFill(demandOrderId, allocationId, count) {
    return this.request(`/demand-orders/${demandOrderId}/sub-allocations/${allocationId}/record-fill`, { method: 'POST', body: { count } });
  }

  reclaimSubAllocation(demandOrderId, allocationId, count) {
    return this.request(`/demand-orders/${demandOrderId}/sub-allocations/${allocationId}/reclaim`, { method: 'POST', body: { count } });
  }

  // ---- Workers (Sourcing Officer / Compliance Officer / Agency Manager / Admin) ----
  listWorkers({ search = '', specialty = '', corridor = '', availability = '', readiness = '' } = {}) {
    const params = new URLSearchParams({ search, specialty, corridor, availability, readiness });
    return this.request(`/workers?${params.toString()}`);
  }

  createWorker(payload) {
    return this.request('/workers', { method: 'POST', body: payload });
  }

  mergeDuplicateWorker(existingWorkerId, fields) {
    return this.request(`/workers/${existingWorkerId}/merge-duplicate`, { method: 'POST', body: { fields } });
  }

  getWorker(id) {
    return this.request(`/workers/${id}`);
  }

  addWorkerServiceRecord(workerId, payload) {
    return this.request(`/workers/${workerId}/service-records`, { method: 'POST', body: payload });
  }

  addWorkerNote(workerId, payload) {
    return this.request(`/workers/${workerId}/notes`, { method: 'POST', body: payload });
  }

  // ---- Requisition Matching (Sourcing Officer / Account Officer / Agency Manager / Admin) ----
  listOpenRequisitionsForMatching() {
    return this.request('/requisition-matching/open-requisitions');
  }

  getRequisitionCandidates(requisitionId) {
    return this.request(`/requisition-matching/${requisitionId}/candidates`);
  }

  commitCandidateToRequisition(requisitionId, workerId) {
    return this.request(`/requisition-matching/${requisitionId}/commit`, { method: 'POST', body: { workerId } });
  }

  // ---- Absorption Capacity (Account Officer / Agency Manager / Admin) ----
  listAbsorptionQuotas({ search = '', corridor = '', status = '' } = {}) {
    const params = new URLSearchParams({ search, corridor, status });
    return this.request(`/absorption-capacity?${params.toString()}`);
  }

  getAbsorptionQuota(counterpartyId) {
    return this.request(`/absorption-capacity/${counterpartyId}`);
  }

  adjustAbsorptionQuota(counterpartyId, payload) {
    return this.request(`/absorption-capacity/${counterpartyId}/adjust`, { method: 'POST', body: payload });
  }

  listAbsorptionCommitments(counterpartyId) {
    return this.request(`/absorption-capacity/${counterpartyId}/commitments`);
  }

  // ---- Gates (Processing Staff / Compliance Officer / Admin) ----
  listGates({ search = '', gateType = '', ownerRole = '', corridor = '', blockingStatus = '' } = {}) {
    const params = new URLSearchParams({ search, gateType, ownerRole, corridor, blockingStatus });
    return this.request(`/gates?${params.toString()}`);
  }

  getGate(id) {
    return this.request(`/gates/${id}`);
  }

  recordGateOutcome(id, payload) {
    return this.request(`/gates/${id}/outcome`, { method: 'POST', body: payload });
  }

  overrideGateOutcome(id, payload) {
    return this.request(`/gates/${id}/override`, { method: 'POST', body: payload });
  }

  // ---- Status Refresh Queue (Processing Staff / Compliance Officer / Admin) ----
  listStatusRefreshQueue({ search = '', authority = '', stalenessHorizon = '' } = {}) {
    const params = new URLSearchParams({ search, authority, stalenessHorizon });
    return this.request(`/gates/status-refresh-queue?${params.toString()}`);
  }

  captureGateStatus(id, { observedStatus, observedDate, notes, file }) {
    const form = new FormData();
    form.append('observedStatus', observedStatus);
    form.append('observedDate', observedDate);
    if (notes) form.append('notes', notes);
    if (file) form.append('file', file);
    return this.request(`/gates/${id}/status-capture`, { method: 'POST', body: form });
  }

  logGatePortalCheck(id) {
    return this.request(`/gates/${id}/log-portal-check`, { method: 'POST' });
  }

  batchRefreshGateCheck(gateInstanceIds) {
    return this.request('/gates/batch-refresh-check', { method: 'POST', body: { gateInstanceIds } });
  }

  // ---- Worker Credentials (Compliance Officer / Processing Staff / Admin) ----
  listWorkerCredentials({ search = '', credentialType = '', provenance = '', verificationState = '', corridorScope = '' } = {}) {
    const params = new URLSearchParams({ search, credentialType, provenance, verificationState, corridorScope });
    return this.request(`/worker-credentials?${params.toString()}`);
  }

  createWorkerCredential({ workerId, credentialType, issuingAuthority, issueDate, expiryDate, transferableScope, file }) {
    const form = new FormData();
    form.append('workerId', workerId);
    form.append('credentialType', credentialType);
    form.append('issuingAuthority', issuingAuthority);
    form.append('issueDate', issueDate);
    if (expiryDate) form.append('expiryDate', expiryDate);
    form.append('transferableScope', transferableScope);
    form.append('file', file);
    return this.request('/worker-credentials', { method: 'POST', body: form });
  }

  markCredentialVoid(id, reason) {
    return this.request(`/worker-credentials/${id}/void`, { method: 'POST', body: { reason } });
  }

  viewCredentialEvidence(id) {
    return this.requestBlob(`/worker-credentials/${id}/evidence`);
  }

  listCredentialAccessLogs(id) {
    return this.request(`/worker-credentials/${id}/access-logs`);
  }

  rejectCredentialDocument(id, { reason, instructions }) {
    return this.request(`/worker-credentials/${id}/reject`, { method: 'POST', body: { reason, instructions } });
  }

  listCredentialRejections(id) {
    return this.request(`/worker-credentials/${id}/rejections`);
  }

  confirmCredentialExtraction(id, { issuingAuthority, holderName, documentNumber, issueDate, expiryDate }) {
    return this.request(`/worker-credentials/${id}/confirm-extraction`, {
      method: 'POST',
      body: { issuingAuthority, holderName, documentNumber, issueDate, expiryDate },
    });
  }

  // ---- Expiry Collision Watchlist (Compliance Officer / Agency Manager / Admin) ----
  listExpiryWatchlist({ search = '', severity = '', credentialType = '', corridor = '' } = {}) {
    const params = new URLSearchParams({ search, severity, credentialType, corridor });
    return this.request(`/expiry-watchlist?${params.toString()}`);
  }

  sendExpiryAlert(credentialId) {
    return this.request(`/expiry-watchlist/${credentialId}/send-alert`, { method: 'POST' });
  }

  // ---- Candidate Portal (Candidate) ----
  getMyCandidateDashboard() {
    return this.request('/candidate/dashboard');
  }

  listMyCandidateTasks() {
    return this.request('/candidate/tasks');
  }

  uploadCandidateTaskDocument(taskId, { expiryDate, file }) {
    const form = new FormData();
    if (expiryDate) form.append('expiryDate', expiryDate);
    form.append('file', file);
    return this.request(`/candidate/tasks/${taskId}/documents`, { method: 'POST', body: form });
  }

  getMyConsentStatus() {
    return this.request('/candidate/consent');
  }

  grantCrossBorderConsent({ signatureName, consentAgreed }) {
    return this.request('/candidate/consent/grant', { method: 'POST', body: { signatureName, consentAgreed } });
  }

  revokeCrossBorderConsent({ acknowledgeImpact }) {
    return this.request('/candidate/consent/revoke', { method: 'POST', body: { acknowledgeImpact } });
  }

  submitVideoIntroduction({ durationSeconds, videoBlob, fileName }) {
    const form = new FormData();
    form.append('durationSeconds', String(durationSeconds));
    form.append('video', videoBlob, fileName);
    return this.request('/candidate/consent/video-introduction', { method: 'POST', body: form });
  }

  // ---- Counterparty Portal — Employer Accreditation (Counterparty Contact) ----
  getMyAccreditationOverview() {
    return this.request('/counterparty-portal/accreditation');
  }

  uploadAccreditationEvidence({ credentialType, issueDate, expiryDate, file }) {
    const form = new FormData();
    form.append('credentialType', credentialType);
    form.append('issueDate', issueDate);
    form.append('expiryDate', expiryDate);
    form.append('file', file);
    return this.request('/counterparty-portal/accreditation/evidence', { method: 'POST', body: form });
  }

  activateLicence(credentialId, { facilityLicenseNumber, employmentCommencementDate, file }) {
    const form = new FormData();
    form.append('facilityLicenseNumber', facilityLicenseNumber);
    form.append('employmentCommencementDate', employmentCommencementDate);
    if (file) form.append('file', file);
    return this.request(`/counterparty-portal/accreditation/${credentialId}/activate`, { method: 'POST', body: form });
  }

  // ---- Counterparty Portal — Requisitions & Candidate Review (Counterparty Contact) ----
  listMyAssignedRequisitions() {
    return this.request('/counterparty-portal/requisitions');
  }

  getCandidateReview(requisitionId) {
    return this.request(`/counterparty-portal/requisitions/${requisitionId}`);
  }

  getCandidateVideo(requisitionId) {
    return this.requestBlob(`/counterparty-portal/requisitions/${requisitionId}/video`);
  }

  recordSelectionDecision(requisitionId, payload) {
    const form = new FormData();
    form.append('decision', payload.decision);
    if (payload.notes) form.append('notes', payload.notes);
    if (payload.targetStartDate) form.append('targetStartDate', payload.targetStartDate);
    if (payload.rejectionCategory) form.append('rejectionCategory', payload.rejectionCategory);
    if (payload.offeredPosition) form.append('offeredPosition', payload.offeredPosition);
    if (payload.offeredSalary) form.append('offeredSalary', payload.offeredSalary);
    if (payload.offeredCurrency) form.append('offeredCurrency', payload.offeredCurrency);
    if (payload.file) form.append('file', payload.file);
    return this.request(`/counterparty-portal/requisitions/${requisitionId}/decision`, { method: 'POST', body: form });
  }

  // ---- Pack Governance (Admin / Compliance Officer) ----
  listPackVersions({ packType = '', status = '' } = {}) {
    const params = new URLSearchParams({ packType, status });
    return this.request(`/pack-governance/packs?${params.toString()}`);
  }

  getPackVersion(id) {
    return this.request(`/pack-governance/packs/${id}`);
  }

  validatePackDefinition({ targetVersion, file }) {
    const form = new FormData();
    form.append('targetVersion', targetVersion);
    form.append('file', file);
    return this.request('/pack-governance/packs/validate', { method: 'POST', body: form });
  }

  activatePackDefinition({ targetVersion, file }) {
    const form = new FormData();
    form.append('targetVersion', targetVersion);
    form.append('file', file);
    return this.request('/pack-governance/packs', { method: 'POST', body: form });
  }

  deletePackVersion(id) {
    return this.request(`/pack-governance/packs/${id}`, { method: 'DELETE' });
  }

  // ---- Cost Ledger & Attestation (Agency Manager / Admin / Account Officer) ----
  listCostLedgerEntries({ search = '', payer = '', corridor = '', dateFrom = '', dateTo = '' } = {}) {
    const params = new URLSearchParams({ search, payer, corridor, dateFrom, dateTo });
    return this.request(`/cost-ledger/entries?${params.toString()}`);
  }

  getCostLedgerSummary({ corridor = '' } = {}) {
    const params = new URLSearchParams({ corridor });
    return this.request(`/cost-ledger/summary?${params.toString()}`);
  }

  listCostLedgerDeployments({ search = '', corridor = '' } = {}) {
    const params = new URLSearchParams({ search, corridor });
    return this.request(`/cost-ledger/deployments?${params.toString()}`);
  }

  getCostLedgerEntry(id) {
    return this.request(`/cost-ledger/entries/${id}`);
  }

  addCostLedgerEntry({ deploymentId, gateInstanceId, amount, currency, incurredDate, payer }) {
    return this.request('/cost-ledger/entries', {
      method: 'POST',
      body: { deploymentId, gateInstanceId: gateInstanceId || undefined, amount, currency, incurredDate, payer: payer || undefined },
    });
  }

  overrideCostLedgerPayer(entryId, { newPayer, justification, file }) {
    const form = new FormData();
    form.append('newPayer', newPayer);
    form.append('justification', justification);
    if (file) form.append('file', file);
    return this.request(`/cost-ledger/entries/${entryId}/override-payer`, { method: 'POST', body: form });
  }

  generateNoFeeAttestation(deploymentId) {
    return this.request('/cost-ledger/attestations', { method: 'POST', body: { deploymentId } });
  }

  downloadAttestation(id) {
    return this.requestBlob(`/cost-ledger/attestations/${id}/download`);
  }

  // ---- Tenant SLA & Horizon Settings (Admin) ----
  getTenantSettings() {
    return this.request('/tenant-settings');
  }

  updateTenantSettings({ expiryHorizonDays, stalenessThresholdDays, slaTargetDays }) {
    return this.request('/tenant-settings', {
      method: 'PUT',
      body: { expiryHorizonDays, stalenessThresholdDays, slaTargetDays },
    });
  }

  uploadAgencyLicenseRenewal({ licenseNumber, issuingRegulator, issueDate, expiryDate, file }) {
    const form = new FormData();
    form.append('licenseNumber', licenseNumber);
    form.append('issuingRegulator', issuingRegulator);
    if (issueDate) form.append('issueDate', issueDate);
    form.append('expiryDate', expiryDate);
    if (file) form.append('file', file);
    return this.request('/tenant-settings/license', { method: 'POST', body: form });
  }

  // ---- Regulator Registry & Fee Schedule (Admin / Compliance Officer) ----
  listRegulators({ search = '', jurisdiction = '', ingestionMode = '' } = {}) {
    const params = new URLSearchParams({ search, jurisdiction, ingestionMode });
    return this.request(`/regulators?${params.toString()}`);
  }

  getRegulator(id) {
    return this.request(`/regulators/${id}`);
  }

  createRegulator(payload) {
    return this.request('/regulators', { method: 'POST', body: payload });
  }

  updateRegulator(id, payload) {
    return this.request(`/regulators/${id}`, { method: 'PATCH', body: payload });
  }

  updateRegulatorFeeSchedule(id, payload) {
    return this.request(`/regulators/${id}/fee-schedule`, { method: 'POST', body: payload });
  }

  flagRegulatorProcessChange(id, note) {
    return this.request(`/regulators/${id}/flag-process-change`, { method: 'POST', body: { note } });
  }

  // ---- Compliance Audit Trail (Admin / Agency Manager / Compliance Officer) ----
  listAuditEvents({ search = '', principal = '', entityType = '', eventType = '', dateFrom = '', dateTo = '' } = {}) {
    const params = new URLSearchParams({ search, principal, entityType, eventType, dateFrom, dateTo });
    return this.request(`/audit-log?${params.toString()}`);
  }

  getAuditEvent(id) {
    return this.request(`/audit-log/${id}`);
  }

  exportAuditTrail({ search = '', principal = '', entityType = '', eventType = '', dateFrom = '', dateTo = '' } = {}) {
    const params = new URLSearchParams({ search, principal, entityType, eventType, dateFrom, dateTo });
    return this.requestBlob(`/audit-log/export?${params.toString()}`);
  }

  // ---- Critical Path Pipeline (Agency Manager / Compliance Officer / Account Officer / Admin) ----
  getCriticalPathPipeline({ search = '', corridor = '', bindingConstraintType = '', riskState = '' } = {}) {
    const params = new URLSearchParams({ search, corridor, bindingConstraintType, riskState });
    return this.request(`/critical-path/pipeline?${params.toString()}`);
  }

  getDeploymentTimeline(deploymentId) {
    return this.request(`/critical-path/deployments/${deploymentId}/timeline`);
  }

  listChaseActions(deploymentId) {
    return this.request(`/critical-path/deployments/${deploymentId}/chase-actions`);
  }

  dispatchChaseAction(deploymentId, payload) {
    return this.request(`/critical-path/deployments/${deploymentId}/chase-actions`, { method: 'POST', body: payload });
  }

  getMigrationCandidates(deploymentId) {
    return this.request(`/critical-path/deployments/${deploymentId}/migration-candidates`);
  }

  migratePackVersion(deploymentId, payload) {
    return this.request(`/critical-path/deployments/${deploymentId}/migrate-pack`, { method: 'POST', body: payload });
  }

  // ---- Pipeline Health & Slippage Analytics (Agency Manager / Admin) ----
  getPipelineSlippageAnalytics({ corridor = '', timeframe = 'YTD' } = {}) {
    const params = new URLSearchParams({ corridor, timeframe });
    return this.request(`/pipeline-analytics?${params.toString()}`);
  }

  exportPipelineSlippageAnalyticsPdf({ corridor = '', timeframe = 'YTD' } = {}) {
    const params = new URLSearchParams({ corridor, timeframe });
    return this.requestBlob(`/pipeline-analytics/export?${params.toString()}`);
  }

  // ---- Candidate Portal — Cost Dispute & Cost Statement (Candidate) ----
  listMyCostLedgerEntries() {
    return this.request('/candidate/cost-ledger/entries');
  }

  flagCostDispute(entryId, reason) {
    return this.request(`/candidate/cost-ledger/entries/${entryId}/dispute`, { method: 'POST', body: { reason } });
  }

  getMyCostStatement() {
    return this.request('/candidate/cost-ledger/statement');
  }

  generateMyCostStatementPdf() {
    return this.request('/candidate/cost-ledger/statement/generate', { method: 'POST' });
  }

  downloadMyCostStatement(id) {
    return this.requestBlob(`/candidate/cost-ledger/statement/${id}/download`);
  }
}

export default new ApiService();
